import { LDClient } from 'launchdarkly-react-client-sdk';

import { TemporaryData } from '../../gql/generated/graphql';
import { getCampaignTraits } from '../../utils/campaignParams';
import { getAttributionContext } from '../../utils/clickIds';
import { formatPhoneNumberWithCountryCode } from '../../utils/strings';
import { GTM, GTMEvent } from './googleTagManager';
import hotjar, { HotjarEvents } from './hotjar';
import { RudderEvent, identify, rudderanalytics } from './rudderstack';

// Meta dedupes on (event_name, event_id); a shared fallback id would merge different visitors
const conversionEventId = (tempInfoId: string | null | undefined, event: GTMEvent) =>
  tempInfoId ? `${tempInfoId}_${event}` : crypto.randomUUID();

export const trackFlowStart = (
  ldClient: LDClient | undefined,
  tempInfoId: string,
  search: string,
  tempInfoData?: TemporaryData,
) => {
  const eventId = conversionEventId(tempInfoId, GTMEvent.StartFlow);
  ldClient?.track('Flow Entry', {});
  GTM.track(GTMEvent.StartFlow, { event_id: eventId });
  hotjar.track(HotjarEvents.FlowEntry);
  if (tempInfoData?.vin) {
    hotjar.identify(tempInfoData.vin, {
      temp_info_id: tempInfoId,
      state: tempInfoData.license_plate_state || undefined,
    });
  }

  if (tempInfoId) {
    identify({ guid: tempInfoId, ...getCampaignTraits(search) });
  }
  rudderanalytics.track(
    RudderEvent.RStackWebStartFlow,
    { event_id: eventId },
    getAttributionContext(),
  );
  rudderanalytics.track(RudderEvent.VinProvided, {
    year: tempInfoData?.year || undefined,
    make: tempInfoData?.make || undefined,
    model: tempInfoData?.model || undefined,
    vin: tempInfoData?.vin || undefined,
    zip: tempInfoData?.zip || undefined,
    license_plate: tempInfoData?.license_plate_number || undefined,
    license_plate_state: tempInfoData?.license_plate_state || undefined,
    fuel_type: tempInfoData?.fuel_type || undefined,
    vehicle_type: tempInfoData?.vehicle_type || undefined,
  });
};

export const trackLead = ({
  tempInfoId,
  email,
  phone_number,
}: {
  tempInfoId: string | null | undefined;
  email?: string;
  phone_number?: string;
}) => {
  const eventId = conversionEventId(tempInfoId, GTMEvent.Lead);
  GTM.track(GTMEvent.Lead, { event_id: eventId });

  identify({
    email,
    phone: formatPhoneNumberWithCountryCode(phone_number),
  });
  rudderanalytics.track(
    RudderEvent.RStackWebEstimate,
    { event_id: eventId },
    getAttributionContext(),
  );
};

export const trackEnhancedLead = ({
  tempInfoId,
  email,
  phone_number,
  first_name,
  last_name,
}: {
  tempInfoId: string | null | undefined;
  email: string;
  phone_number: string;
  first_name: string;
  last_name: string;
}) => {
  const eventId = conversionEventId(tempInfoId, GTMEvent.EnhancedLead);
  GTM.track(GTMEvent.EnhancedLead, { event_id: eventId });

  identify({
    email,
    phone: formatPhoneNumberWithCountryCode(phone_number),
    firstName: first_name,
    lastName: last_name,
  });
  rudderanalytics.track(
    RudderEvent.RStackWebEstimateEnhanced,
    { event_id: eventId },
    getAttributionContext(),
  );
};

export const trackFlowFinish = (tempInfoId: string | null | undefined) => {
  const eventId = conversionEventId(tempInfoId, GTMEvent.FinishFlow);
  GTM.track(GTMEvent.FinishFlow, { event_id: eventId });

  rudderanalytics.track(
    RudderEvent.RStackWebFinishFlow,
    { event_id: eventId },
    getAttributionContext(),
  );
};
